import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation, useParams } from "react-router-dom";
import { getStaff, createStaff, updateStaff, deleteStaff, getVenues, sendCrewInvitation, sendBulkInvitations, extractErrorMessage } from "../lib/api";
import api from "../lib/api";
// EVENTSFLO-PROFILE-PHOTO-SIGNED-URL-V1 — image rendering goes through
// short-lived signed URLs (legacy public route is now auth-gated).
import { SignedStaffPhoto } from "../lib/SignedStaffPhoto";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { DestructiveConfirmDialog } from "../components/shared/DestructiveConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CountrySelect } from "../components/shared/CountrySelect";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "sonner";
import {
  Plus,
  UserCheck,
  Pencil,
  Trash2,
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Shield,
  CreditCard,
  Settings,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Award,
  Car,
  Search,
  Filter,
  X,
  Camera,
  Key,
  Smartphone,
  Lock,
  Info,
  Send,
  MailCheck,
  UserPlus,
  FileText,
  Languages,
} from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { CrewDocumentManager } from "../components/crew/CrewDocumentManager";
import { StaffSuitabilityTabs } from "../components/staff/StaffSuitabilityTabs";
import { CrewProfileTabs } from "../components/staff/CrewProfileTabs";
import { InviteCrewModal } from "../components/crew/InviteCrewModal";
import { useTableDisplaySettings, getZoomClasses } from "../hooks/useZoom";
import { ZoomControl } from "../components/common/ZoomControl";
import PageZoomContainer from "../components/layout/PageZoomContainer";

// Feature flag for new crew profile UI
const USE_NEW_CREW_PROFILE_UI = true;

// Helper to format date for input
const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  return dateStr.split("T")[0];
};

// Initial form state matching uTrac fields
const getInitialFormData = () => ({
  // Staff Details (Middle Column in uTrac)
  first_name: "",
  last_name: "",
  gender: "",
  date_of_birth: "",
  place_of_birth: "",
  phone: "",           // Mobile Number (primary)
  phone_home: "",      // Home Number
  email: "",
  // Address
  address: {
    line1: "",
    line2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  },
  // Employment Info (Right Column in uTrac)
  status: "active",
  employee_id: "",
  employment_type: "casual",
  national_insurance_number: "",  // Social Security equivalent
  hourly_rate_default: "",
  // Right to Work
  right_to_work_status: "not_checked",
  right_to_work_expiry: "",
  right_to_work_doc_type: "",
  // SIA License
  sia_license: {
    license_number: "",
    license_type: "",
    expiry_date: "",
    is_verified: false,
  },
  // Skills / Labels
  skills: "",
  certifications: [],
  // Preference Value (for Shift Crew Planner sorting)
  preference_value: 0,
  // Comments / Notes
  notes: "",
  // User Management (Left Column in uTrac)
  access_controls: {
    can_login: false,
    pin_code: "",
    app_notifications_enabled: true,
  },
  // Operational
  operational_prefs: {
    preferred_venue_ids: [],
    max_hours_per_week: "",
    travel_radius_km: "",
    has_own_transport: false,
    uniform_size: "",
    dietary_requirements: "",
  },
  // Emergency Contact
  emergency_contact: {
    name: "",
    phone: "",
    relationship: "",
  },
  // Bank Details
  bank_details: {
    account_name: "",
    sort_code: "",
    account_number: "",
  },
  // Profile photo
  profile_photo_url: "",
  // EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
  // Admin-set Crew Portal UI language. "" = "use crew choice / browser
  // default" (cleared / null on the backend). Allowed real values are
  // "en", "fr" and "ru".
  preferred_portal_language: "",
});

// Crew Form Dialog - uTrac 3-Column Layout with Documents Tab
function CrewFormDialog({ 
  open, 
  onOpenChange, 
  staff, 
  onSuccess, 
  venues = [] 
}) {
  const { currencySymbol } = useFormatters();
  const [formData, setFormData] = useState(getInitialFormData());
  const [submitting, setSubmitting] = useState(false);
  const [newCert, setNewCert] = useState({ name: "", issuer: "", expiry_date: "" });
  // EF-CREW-DOCUMENT-MANAGEMENT-V1: Tab state for Details vs Documents
  const [activeTab, setActiveTab] = useState("details");

  // Reset to details tab when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveTab("details");
    }
  }, [open]);

  // Populate form when editing
  useEffect(() => {
    if (staff) {
      setFormData({
        first_name: staff.first_name || "",
        last_name: staff.last_name || "",
        gender: staff.gender || "",
        date_of_birth: formatDateForInput(staff.date_of_birth) || "",
        place_of_birth: staff.place_of_birth || "",
        phone: staff.phone || "",
        phone_home: staff.phone_home || "",
        email: staff.email || "",
        address: {
          line1: staff.address?.line1 || "",
          line2: staff.address?.line2 || "",
          city: staff.address?.city || "",
          postcode: staff.address?.postcode || "",
          country: staff.address?.country || "United Kingdom",
        },
        status: staff.status || "active",
        employee_id: staff.employee_id || "",
        employment_type: staff.employment_type || "casual",
        national_insurance_number: staff.national_insurance_number || "",
        hourly_rate_default: staff.hourly_rate_default?.toString() || "",
        right_to_work_status: staff.right_to_work_status || "not_checked",
        right_to_work_expiry: formatDateForInput(staff.right_to_work_expiry) || "",
        right_to_work_doc_type: staff.right_to_work_doc_type || "",
        sia_license: {
          license_number: staff.sia_license?.license_number || "",
          license_type: staff.sia_license?.license_type || "",
          expiry_date: formatDateForInput(staff.sia_license?.expiry_date) || "",
          is_verified: staff.sia_license?.is_verified || false,
        },
        skills: staff.skills?.join(", ") || "",
        certifications: staff.certifications || [],
        preference_value: staff.preference_value || 0,
        notes: staff.notes || "",
        access_controls: {
          can_login: staff.access_controls?.can_login || false,
          pin_code: staff.access_controls?.pin_code || "",
          app_notifications_enabled: staff.access_controls?.app_notifications_enabled ?? true,
        },
        operational_prefs: {
          preferred_venue_ids: staff.operational_prefs?.preferred_venue_ids || [],
          max_hours_per_week: staff.operational_prefs?.max_hours_per_week?.toString() || "",
          travel_radius_km: staff.operational_prefs?.travel_radius_km?.toString() || "",
          has_own_transport: staff.operational_prefs?.has_own_transport || false,
          uniform_size: staff.operational_prefs?.uniform_size || "",
          dietary_requirements: staff.operational_prefs?.dietary_requirements || "",
        },
        emergency_contact: {
          name: staff.emergency_contact?.name || "",
          phone: staff.emergency_contact?.phone || "",
          relationship: staff.emergency_contact?.relationship || "",
        },
        bank_details: {
          account_name: staff.bank_details?.account_name || "",
          sort_code: staff.bank_details?.sort_code || "",
          account_number: staff.bank_details?.account_number || "",
        },
        profile_photo_url: staff.profile_photo_url || "",
        // EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
        preferred_portal_language: staff.preferred_portal_language || "",
      });
    } else {
      setFormData(getInitialFormData());
    }
  }, [staff, open]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const addCertification = () => {
    if (!newCert.name) return;
    setFormData((prev) => ({
      ...prev,
      certifications: [...prev.certifications, { ...newCert }],
    }));
    setNewCert({ name: "", issuer: "", expiry_date: "" });
  };

  const removeCertification = (index) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || null,
        date_of_birth: formData.date_of_birth || null,
        // EVENTSFLO-CREW-PLACE-OF-BIRTH-PROFILE-AND-AI-ONBOARDING-V1:
        // free-text city/town. Empty string is sent as `null` so the
        // backend stores the explicit "cleared" state, not an empty string.
        place_of_birth: (formData.place_of_birth || "").trim() || null,
        gender: formData.gender || null,
        profile_photo_url: formData.profile_photo_url || null,
        status: formData.status,
        skills: formData.skills ? formData.skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
        preference_value: parseInt(formData.preference_value) || 0,
        hourly_rate_default: formData.hourly_rate_default ? parseFloat(formData.hourly_rate_default) : null,
        notes: formData.notes || null,
        employee_id: formData.employee_id || null,
        national_insurance_number: formData.national_insurance_number || null,
        right_to_work_status: formData.right_to_work_status,
        right_to_work_expiry: formData.right_to_work_expiry || null,
        right_to_work_doc_type: formData.right_to_work_doc_type || null,
        employment_type: formData.employment_type,
        certifications: formData.certifications,
        // EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
        // Always submit so an admin can also clear an existing value.
        // Empty string → null (server default / "use crew choice").
        preferred_portal_language: formData.preferred_portal_language || null,
      };

      // Add sub-objects only if they have data
      if (formData.address.line1 || formData.address.city || formData.address.postcode) {
        data.address = formData.address;
      }

      if (formData.emergency_contact.name || formData.emergency_contact.phone) {
        data.emergency_contact = formData.emergency_contact;
      }

      if (formData.sia_license.license_number) {
        data.sia_license = {
          ...formData.sia_license,
          expiry_date: formData.sia_license.expiry_date || null,
        };
      }

      if (formData.operational_prefs.max_hours_per_week || formData.operational_prefs.travel_radius_km || formData.operational_prefs.has_own_transport) {
        data.operational_prefs = {
          ...formData.operational_prefs,
          max_hours_per_week: formData.operational_prefs.max_hours_per_week ? parseInt(formData.operational_prefs.max_hours_per_week) : null,
          travel_radius_km: formData.operational_prefs.travel_radius_km ? parseInt(formData.operational_prefs.travel_radius_km) : null,
        };
      }

      if (formData.access_controls.can_login || formData.access_controls.pin_code) {
        data.access_controls = formData.access_controls;
      }

      if (formData.bank_details.account_name || formData.bank_details.sort_code || formData.bank_details.account_number) {
        data.bank_details = formData.bank_details;
      }

      if (staff) {
        await updateStaff(staff.id, data);
        toast.success("Crew member updated successfully");
      } else {
        await createStaff(data);
        toast.success("Crew member created successfully");
      }

      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 max-w-6xl max-h-[95vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-white/10 bg-surface-elevated/30">
          <DialogTitle className="font-secondary text-lg uppercase tracking-wide flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            {staff ? "Edit Crew Member" : "Add New Crew Member"}
          </DialogTitle>
        </DialogHeader>

        {/* EF-CREW-DOCUMENT-MANAGEMENT-V1: Tabs for Details and Documents */}
        {/* EF-CREW-ROLES-QUALIFICATIONS-HARDENING-V1: Added Suitability tab */}
        {staff ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="mx-6 mt-4 mb-0 bg-surface-elevated/30 border border-white/5 w-fit">
              <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
                <User className="h-4 w-4" />
                Details
              </TabsTrigger>
              <TabsTrigger value="suitability" className="gap-2" data-testid="tab-suitability">
                <Briefcase className="h-4 w-4" />
                Suitability
              </TabsTrigger>
              <TabsTrigger value="documents" className="gap-2" data-testid="tab-documents">
                <FileText className="h-4 w-4" />
                Documents
              </TabsTrigger>
            </TabsList>
            
            {/* Details Tab - The existing form */}
            <TabsContent value="details" className="flex-1 flex flex-col overflow-hidden m-0 p-0">
              <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
                {/* 3-Column Grid Layout (uTrac Style) */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="grid grid-cols-12 gap-6">
              
              {/* LEFT COLUMN: Readiness + Image + User Management */}
              <div className="col-span-3 space-y-4">
                {/* EF-CREW-READINESS-STATUS-V1: Readiness Status Card */}
                <Card className={`border ${
                  staff.readiness_status === 'ready' ? 'bg-emerald-500/10 border-emerald-500/30' :
                  staff.readiness_status === 'document_expiring' ? 'bg-amber-500/10 border-amber-500/30' :
                  staff.readiness_status === 'document_expired' ? 'bg-red-500/10 border-red-500/30' :
                  staff.readiness_status === 'verification_required' ? 'bg-yellow-500/10 border-yellow-500/30' :
                  'bg-zinc-500/10 border-zinc-500/30'
                }`} data-testid="readiness-card">
                  <CardHeader className="pb-1 pt-3 px-3">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      Shift Readiness
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <div className="flex items-center gap-2">
                      <ReadinessStatusBadge 
                        status={staff.readiness_status || "missing_documents"} 
                        reason={null}
                      />
                    </div>
                    {staff.readiness_reason && (
                      <p className="text-xs text-muted-foreground mt-2" data-testid="readiness-reason">
                        {staff.readiness_reason}
                      </p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Profile Image */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Image</CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <div className="aspect-square bg-surface-base/50 rounded border border-dashed border-white/10 flex items-center justify-center">
                      {formData.profile_photo_url ? (
                        <SignedStaffPhoto
                          staffId={staff?.id}
                          profilePhotoUrl={formData.profile_photo_url}
                          alt="Profile"
                          className="w-full h-full object-cover rounded"
                          fallback={(
                            <div className="text-center text-muted-foreground">
                              <Camera className="h-8 w-8 mx-auto mb-1 opacity-50" />
                              <span className="text-[10px]">No Image</span>
                            </div>
                          )}
                          dataTestId="staff-profile-image"
                        />
                      ) : (
                        <div className="text-center text-muted-foreground">
                          <Camera className="h-8 w-8 mx-auto mb-1 opacity-50" />
                          <span className="text-[10px]">No Image</span>
                        </div>
                      )}
                    </div>
                    <Input
                      placeholder="Image URL"
                      value={formData.profile_photo_url}
                      onChange={(e) => updateField("profile_photo_url", e.target.value)}
                      className="mt-2 text-xs"
                    />
                  </CardContent>
                </Card>

                {/* User Management */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Key className="h-3 w-3" />
                      User Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs flex items-center gap-1">
                        <Smartphone className="h-3 w-3" />
                        App Access
                      </Label>
                      <Switch
                        checked={formData.access_controls.can_login}
                        onCheckedChange={(checked) => updateNestedField("access_controls", "can_login", checked)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Push Notifications</Label>
                      <Switch
                        checked={formData.access_controls.app_notifications_enabled}
                        onCheckedChange={(checked) => updateNestedField("access_controls", "app_notifications_enabled", checked)}
                      />
                    </div>

                    <div>
                      <Label className="text-xs flex items-center gap-1 mb-1">
                        <Lock className="h-3 w-3" />
                        PIN Code
                      </Label>
                      <Input
                        type="text"
                        maxLength={6}
                        placeholder="4-6 digits"
                        value={formData.access_controls.pin_code}
                        onChange={(e) => updateNestedField("access_controls", "pin_code", e.target.value.replace(/\D/g, ""))}
                        className="text-xs font-mono"
                      />
                    </div>

                    {/* EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
                        Admin-settable Crew Portal UI language. Empty
                        value clears the server preference and the
                        Crew Portal falls back to localStorage / `en`. */}
                    <div data-testid="preferred-portal-language-field">
                      <Label className="text-xs flex items-center gap-1 mb-1">
                        <Languages className="h-3 w-3" />
                        Preferred portal language
                      </Label>
                      <Select
                        value={formData.preferred_portal_language || "__none__"}
                        onValueChange={(v) =>
                          updateField(
                            "preferred_portal_language",
                            v === "__none__" ? "" : v,
                          )
                        }
                      >
                        <SelectTrigger
                          className="text-xs h-8"
                          data-testid="preferred-portal-language-trigger"
                        >
                          <SelectValue placeholder="Use crew choice / browser default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            value="__none__"
                            data-testid="preferred-portal-language-option-none"
                          >
                            Use crew choice / browser default
                          </SelectItem>
                          <SelectItem
                            value="en"
                            data-testid="preferred-portal-language-option-en"
                          >
                            en — English
                          </SelectItem>
                          <SelectItem
                            value="fr"
                            data-testid="preferred-portal-language-option-fr"
                          >
                            fr — French
                          </SelectItem>
                          <SelectItem
                            value="ru"
                            data-testid="preferred-portal-language-option-ru"
                          >
                            ru — Russian
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p
                        className="text-[10px] text-muted-foreground mt-1 leading-snug"
                        data-testid="preferred-portal-language-help"
                      >
                        This controls the Crew Portal interface language across
                        devices. It does not change the crew member&apos;s
                        spoken languages.
                      </p>
                    </div>

                    {staff && (
                      <div className="pt-2 border-t border-white/5 text-[10px] text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>App Installed:</span>
                          <span>{formData.access_controls.can_login ? "Yes" : "No"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Last Login:</span>
                          <span>—</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Emergency Contact */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-xs uppercase tracking-wider text-amber-500 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Emergency Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 space-y-2">
                    <Input
                      placeholder="Contact Name"
                      value={formData.emergency_contact.name}
                      onChange={(e) => updateNestedField("emergency_contact", "name", e.target.value)}
                      className="text-xs"
                    />
                    <Input
                      placeholder="Phone Number"
                      value={formData.emergency_contact.phone}
                      onChange={(e) => updateNestedField("emergency_contact", "phone", e.target.value)}
                      className="text-xs"
                    />
                    <Input
                      placeholder="Relationship"
                      value={formData.emergency_contact.relationship}
                      onChange={(e) => updateNestedField("emergency_contact", "relationship", e.target.value)}
                      className="text-xs"
                    />
                  </CardContent>
                </Card>
              </div>

              {/* MIDDLE COLUMN: Crew Details */}
              <div className="col-span-5 space-y-4">
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Crew Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    {/* Name */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">
                          First Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.first_name}
                          onChange={(e) => updateField("first_name", e.target.value)}
                          required
                          className="text-sm"
                          data-testid="staff-first-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">
                          Last Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.last_name}
                          onChange={(e) => updateField("last_name", e.target.value)}
                          required
                          className="text-sm"
                          data-testid="staff-last-name-input"
                        />
                      </div>
                    </div>

                    {/* Gender & DOB */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Gender</Label>
                        <Select value={formData.gender} onValueChange={(value) => updateField("gender", value)}>
                          <SelectTrigger className="text-sm">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="non_binary">Non-binary</SelectItem>
                            <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Date of Birth</Label>
                        <Input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => updateField("date_of_birth", e.target.value)}
                          className="text-sm"
                        />
                        <span className="text-[9px] text-muted-foreground">Format: dd-mm-yyyy</span>
                      </div>
                    </div>

                    {/* EVENTSFLO-CREW-PLACE-OF-BIRTH-PROFILE-AND-AI-ONBOARDING-V1
                        Free-text Place of Birth used by the Crew ID Pack
                        PDF for site-security verification. Clear by emptying
                        the input — empty string is sent as null on save. */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Place of Birth</Label>
                        <Input
                          value={formData.place_of_birth}
                          onChange={(e) => updateField("place_of_birth", e.target.value)}
                          placeholder="e.g. Nice, Tbilisi, Moscou"
                          maxLength={200}
                          className="text-sm"
                          data-testid="staff-place-of-birth-input"
                        />
                        <span className="text-[9px] text-muted-foreground">
                          City / town as printed on the ID document. Used on the Crew ID Pack PDF.
                        </span>
                      </div>
                    </div>

                    {/* Phone Numbers */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">
                          Mobile Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => updateField("phone", e.target.value)}
                          placeholder="+44 7700 900123"
                          className="text-sm"
                          data-testid="staff-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Home Number</Label>
                        <Input
                          value={formData.phone_home}
                          onChange={(e) => updateField("phone_home", e.target.value)}
                          placeholder="01onal"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        required
                        className="text-sm"
                        data-testid="staff-email-input"
                      />
                    </div>

                    {/* Address */}
                    <div className="pt-2 border-t border-white/5">
                      <Label className="text-xs flex items-center gap-1 mb-2">
                        <MapPin className="h-3 w-3" />
                        Address
                      </Label>
                      <div className="space-y-2">
                        <Input
                          placeholder="Address Line 1"
                          value={formData.address.line1}
                          onChange={(e) => updateNestedField("address", "line1", e.target.value)}
                          className="text-sm"
                        />
                        <Input
                          placeholder="Address Line 2"
                          value={formData.address.line2}
                          onChange={(e) => updateNestedField("address", "line2", e.target.value)}
                          className="text-sm"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="City / State"
                            value={formData.address.city}
                            onChange={(e) => updateNestedField("address", "city", e.target.value)}
                            className="text-sm"
                          />
                          <Input
                            placeholder="Post Code"
                            value={formData.address.postcode}
                            onChange={(e) => updateNestedField("address", "postcode", e.target.value.toUpperCase())}
                            className="text-sm"
                          />
                        </div>
                        <CountrySelect
                          value={formData.address.country}
                          onValueChange={(value) => updateNestedField("address", "country", value)}
                          placeholder="Select country..."
                          testId="staff-country-select"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Bank Details */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <CreditCard className="h-3 w-3" />
                      Bank Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-2">
                    <Input
                      placeholder="Account Holder Name"
                      value={formData.bank_details.account_name}
                      onChange={(e) => updateNestedField("bank_details", "account_name", e.target.value)}
                      className="text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Sort Code (XX-XX-XX)"
                        value={formData.bank_details.sort_code}
                        onChange={(e) => updateNestedField("bank_details", "sort_code", e.target.value)}
                        className="text-sm font-mono"
                      />
                      <Input
                        placeholder="Account Number"
                        value={formData.bank_details.account_number}
                        onChange={(e) => updateNestedField("bank_details", "account_number", e.target.value)}
                        className="text-sm font-mono"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* RIGHT COLUMN: Employment Info */}
              <div className="col-span-4 space-y-4">
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      Employment Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    {/* Status */}
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={formData.status} onValueChange={(value) => updateField("status", value)}>
                        <SelectTrigger className="text-sm" data-testid="staff-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Employee No */}
                    <div>
                      <Label className="text-xs">
                        Employee No. <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={formData.employee_id}
                        onChange={(e) => updateField("employee_id", e.target.value)}
                        placeholder="EMP-001"
                        className="text-sm font-mono"
                        data-testid="staff-employee-id"
                      />
                    </div>

                    {/* Employment Type */}
                    <div>
                      <Label className="text-xs">Employment Type</Label>
                      <Select value={formData.employment_type} onValueChange={(value) => updateField("employment_type", value)}>
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Full Time</SelectItem>
                          <SelectItem value="part_time">Part Time</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="contractor">Contractor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* NI Number (Social Security) */}
                    <div>
                      <Label className="text-xs">National Insurance No.</Label>
                      <Input
                        value={formData.national_insurance_number}
                        onChange={(e) => updateField("national_insurance_number", e.target.value.toUpperCase())}
                        placeholder="AB123456C"
                        className="text-sm font-mono"
                      />
                    </div>

                    {/* Hourly Rate */}
                    <div>
                      <Label className="text-xs">Default Hourly Rate ({currencySymbol})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.hourly_rate_default}
                        onChange={(e) => updateField("hourly_rate_default", e.target.value)}
                        placeholder="15.00"
                        className="text-sm"
                      />
                    </div>

                    {/* Comments */}
                    <div>
                      <Label className="text-xs">Comments</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => updateField("notes", e.target.value)}
                        placeholder="Additional notes..."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Compliance */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      Compliance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    {/* RTW Status */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Right to Work</Label>
                        <Select value={formData.right_to_work_status} onValueChange={(value) => updateField("right_to_work_status", value)}>
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_checked">Not Checked</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="verified">Verified</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">RTW Expiry</Label>
                        <Input
                          type="date"
                          value={formData.right_to_work_expiry}
                          onChange={(e) => updateField("right_to_work_expiry", e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    </div>

                    {/* SIA License */}
                    <div className="pt-2 border-t border-white/5">
                      <Label className="text-xs flex items-center gap-1 mb-2">
                        <Award className="h-3 w-3 text-amber-500" />
                        SIA License
                      </Label>
                      <div className="space-y-2">
                        <Input
                          placeholder="License Number (16 digits)"
                          value={formData.sia_license.license_number}
                          onChange={(e) => updateNestedField("sia_license", "license_number", e.target.value)}
                          className="text-sm font-mono"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Select value={formData.sia_license.license_type} onValueChange={(value) => updateNestedField("sia_license", "license_type", value)}>
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder="Type..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="door_supervisor">Door Supervisor</SelectItem>
                              <SelectItem value="security_guard">Security Guard</SelectItem>
                              <SelectItem value="cctv">CCTV</SelectItem>
                              <SelectItem value="close_protection">Close Protection</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="date"
                            value={formData.sia_license.expiry_date}
                            onChange={(e) => updateNestedField("sia_license", "expiry_date", e.target.value)}
                            className="text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={formData.sia_license.is_verified}
                            onCheckedChange={(checked) => updateNestedField("sia_license", "is_verified", checked)}
                          />
                          <Label className="text-xs">Verified</Label>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Skills / Labels */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Award className="h-3 w-3" />
                      Skills & Certifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    <div className="grid grid-cols-[1fr_80px] gap-2">
                      <div>
                        <Label className="text-xs">Skills (comma separated)</Label>
                        <Input
                          value={formData.skills}
                          onChange={(e) => updateField("skills", e.target.value)}
                          placeholder="Security, First Aid, Bar Crew"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">MET Score (0-99)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="99"
                          value={formData.preference_value}
                          onChange={(e) => updateField("preference_value", e.target.value)}
                          placeholder="0"
                          className="text-sm"
                          data-testid="staff-met-score"
                        />
                      </div>
                    </div>

                    {/* Certifications */}
                    {formData.certifications.length > 0 && (
                      <div className="space-y-1">
                        {formData.certifications.map((cert, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-surface-base/50 px-2 py-1 rounded text-xs">
                            <span>{cert.name}</span>
                            <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => removeCertification(idx)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-1">
                      <Input
                        placeholder="Add certification..."
                        value={newCert.name}
                        onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                        className="text-xs flex-1"
                      />
                      <Button type="button" variant="outline" size="sm" onClick={addCertification} className="px-2">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Operational Preferences */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Settings className="h-3 w-3" />
                      Operational
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Max Hours/Week</Label>
                        <Input
                          type="number"
                          placeholder="40"
                          value={formData.operational_prefs.max_hours_per_week}
                          onChange={(e) => updateNestedField("operational_prefs", "max_hours_per_week", e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Travel Radius (km)</Label>
                        <Input
                          type="number"
                          placeholder="50"
                          value={formData.operational_prefs.travel_radius_km}
                          onChange={(e) => updateNestedField("operational_prefs", "travel_radius_km", e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 pt-1">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formData.operational_prefs.has_own_transport}
                          onCheckedChange={(checked) => updateNestedField("operational_prefs", "has_own_transport", checked)}
                        />
                        <Label className="text-xs flex items-center gap-1">
                          <Car className="h-3 w-3" />
                          Own Transport
                        </Label>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Uniform Size</Label>
                      <Select value={formData.operational_prefs.uniform_size} onValueChange={(value) => updateNestedField("operational_prefs", "uniform_size", value)}>
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="XS">XS</SelectItem>
                          <SelectItem value="S">S</SelectItem>
                          <SelectItem value="M">M</SelectItem>
                          <SelectItem value="L">L</SelectItem>
                          <SelectItem value="XL">XL</SelectItem>
                          <SelectItem value="XXL">XXL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

                {/* Footer - Action Buttons */}
                <DialogFooter className="px-6 py-4 border-t border-white/10 bg-surface-elevated/30 flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground px-8" disabled={submitting} data-testid="staff-submit-btn">
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save Changes
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
            
            {/* Suitability Tab - EF-CREW-ROLES-QUALIFICATIONS-HARDENING-V1 */}
            <TabsContent value="suitability" className="flex-1 overflow-y-auto m-0 p-6">
              <StaffSuitabilityTabs 
                staffId={staff.id}
                staffData={staff}
                onUpdate={() => loadStaffById(staff.id)}
              />
            </TabsContent>
            
            {/* Documents Tab - EF-CREW-DOCUMENT-MANAGEMENT-V1 */}
            <TabsContent value="documents" className="flex-1 overflow-y-auto m-0 p-6">
              <CrewDocumentManager 
                staffId={staff.id} 
                staffName={`${staff.first_name} ${staff.last_name}`} 
              />
            </TabsContent>
          </Tabs>
        ) : (
          /* New staff - no tabs, just the form */
          <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
            {/* 3-Column Grid Layout (uTrac Style) */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-12 gap-6">
              
              {/* LEFT COLUMN: Image + User Management */}
              <div className="col-span-3 space-y-4">
                {/* Profile Image */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Image</CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <div className="aspect-square bg-surface-base/50 rounded border border-dashed border-white/10 flex items-center justify-center">
                      {formData.profile_photo_url ? (
                        <SignedStaffPhoto
                          staffId={staff?.id}
                          profilePhotoUrl={formData.profile_photo_url}
                          alt="Profile"
                          className="w-full h-full object-cover rounded"
                          fallback={(
                            <div className="text-center text-muted-foreground">
                              <Camera className="h-8 w-8 mx-auto mb-1 opacity-50" />
                              <span className="text-[10px]">No Image</span>
                            </div>
                          )}
                          dataTestId="staff-profile-image"
                        />
                      ) : (
                        <div className="text-center text-muted-foreground">
                          <Camera className="h-8 w-8 mx-auto mb-1 opacity-50" />
                          <span className="text-[10px]">No Image</span>
                        </div>
                      )}
                    </div>
                    <Input
                      placeholder="Image URL"
                      value={formData.profile_photo_url}
                      onChange={(e) => updateField("profile_photo_url", e.target.value)}
                      className="mt-2 text-xs"
                    />
                  </CardContent>
                </Card>

                {/* User Management */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Key className="h-3 w-3" />
                      User Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs flex items-center gap-1">
                        <Smartphone className="h-3 w-3" />
                        App Access
                      </Label>
                      <Switch
                        checked={formData.access_controls.can_login}
                        onCheckedChange={(checked) => updateNestedField("access_controls", "can_login", checked)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Push Notifications</Label>
                      <Switch
                        checked={formData.access_controls.app_notifications_enabled}
                        onCheckedChange={(checked) => updateNestedField("access_controls", "app_notifications_enabled", checked)}
                      />
                    </div>

                    <div>
                      <Label className="text-xs flex items-center gap-1 mb-1">
                        <Lock className="h-3 w-3" />
                        PIN Code
                      </Label>
                      <Input
                        type="text"
                        maxLength={6}
                        placeholder="4-6 digits"
                        value={formData.access_controls.pin_code}
                        onChange={(e) => updateNestedField("access_controls", "pin_code", e.target.value.replace(/\D/g, ""))}
                        className="text-xs font-mono"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Emergency Contact */}
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-xs uppercase tracking-wider text-amber-500 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Emergency Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 space-y-2">
                    <Input
                      placeholder="Contact Name"
                      value={formData.emergency_contact.name}
                      onChange={(e) => updateNestedField("emergency_contact", "name", e.target.value)}
                      className="text-xs"
                    />
                    <Input
                      placeholder="Phone Number"
                      value={formData.emergency_contact.phone}
                      onChange={(e) => updateNestedField("emergency_contact", "phone", e.target.value)}
                      className="text-xs"
                    />
                    <Input
                      placeholder="Relationship"
                      value={formData.emergency_contact.relationship}
                      onChange={(e) => updateNestedField("emergency_contact", "relationship", e.target.value)}
                      className="text-xs"
                    />
                  </CardContent>
                </Card>
              </div>

              {/* MIDDLE COLUMN: Crew Details - minimal for new staff */}
              <div className="col-span-5 space-y-4">
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Crew Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    {/* Name */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">
                          First Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.first_name}
                          onChange={(e) => updateField("first_name", e.target.value)}
                          required
                          className="text-sm"
                          data-testid="staff-first-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">
                          Last Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.last_name}
                          onChange={(e) => updateField("last_name", e.target.value)}
                          required
                          className="text-sm"
                          data-testid="staff-last-name-input"
                        />
                      </div>
                    </div>

                    {/* Phone & Email */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">
                          Mobile Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => updateField("phone", e.target.value)}
                          placeholder="+44 7700 900123"
                          className="text-sm"
                          data-testid="staff-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => updateField("email", e.target.value)}
                          required
                          className="text-sm"
                          data-testid="staff-email-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* RIGHT COLUMN: Employment Info - minimal for new staff */}
              <div className="col-span-4 space-y-4">
                <Card className="bg-surface-elevated/20 border-white/5">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      Employment Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={formData.status} onValueChange={(value) => updateField("status", value)}>
                        <SelectTrigger className="text-sm" data-testid="staff-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">
                        Employee No. <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={formData.employee_id}
                        onChange={(e) => updateField("employee_id", e.target.value)}
                        placeholder="EMP-001"
                        className="text-sm font-mono"
                        data-testid="staff-employee-id"
                      />
                    </div>

                    <div>
                      <Label className="text-xs">Employment Type</Label>
                      <Select value={formData.employment_type} onValueChange={(value) => updateField("employment_type", value)}>
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Full Time</SelectItem>
                          <SelectItem value="part_time">Part Time</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="contractor">Contractor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </div>
              </div>
            </div>

            {/* Footer - Action Buttons for new staff */}
            <DialogFooter className="px-6 py-4 border-t border-white/10 bg-surface-elevated/30 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground px-8" disabled={submitting} data-testid="staff-submit-btn">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Crew Member
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// RTW Status Badge
function RTWStatusBadge({ status }) {
  const config = {
    verified: { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10" },
    pending: { icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10" },
    expired: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
    not_checked: { icon: AlertTriangle, color: "text-muted-foreground", bg: "bg-surface-elevated" },
  };
  const cfg = config[status] || config.not_checked;
  const Icon = cfg.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${cfg.bg} ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {status?.replace("_", " ")}
    </span>
  );
}

// EF-CREW-READINESS-STATUS-V1: Readiness Status Badge
function ReadinessStatusBadge({ status, reason }) {
  const config = {
    ready: { 
      icon: CheckCircle2, 
      color: "text-emerald-400", 
      bg: "bg-emerald-500/10",
      border: "border-emerald-500/30",
      label: "Ready"
    },
    document_expiring: { 
      icon: Clock, 
      color: "text-amber-400", 
      bg: "bg-amber-500/10",
      border: "border-amber-500/30",
      label: "Expiring"
    },
    document_expired: { 
      icon: XCircle, 
      color: "text-red-400", 
      bg: "bg-red-500/10",
      border: "border-red-500/30",
      label: "Expired"
    },
    missing_documents: { 
      icon: AlertTriangle, 
      color: "text-zinc-400", 
      bg: "bg-zinc-500/10",
      border: "border-zinc-500/30",
      label: "Missing Docs"
    },
    verification_required: { 
      icon: Clock, 
      color: "text-yellow-400", 
      bg: "bg-yellow-500/10",
      border: "border-yellow-500/30",
      label: "Pending"
    },
    inactive: { 
      icon: XCircle, 
      color: "text-zinc-500", 
      bg: "bg-zinc-600/10",
      border: "border-zinc-600/30",
      label: "Inactive"
    },
  };
  
  const cfg = config[status] || config.missing_documents;
  const Icon = cfg.icon;
  
  return (
    <div className="flex flex-col gap-0.5">
      <span 
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${cfg.bg} ${cfg.color} border ${cfg.border}`}
        title={reason || status}
        data-testid="readiness-badge"
      >
        <Icon className="h-3 w-3" />
        {cfg.label}
      </span>
      {reason && (
        <span className="text-[9px] text-muted-foreground truncate max-w-[120px]" title={reason}>
          {reason}
        </span>
      )}
    </div>
  );
}

// Main Crew Page Component
export default function StaffPage() {
  const { formatCurrency, currencySymbol } = useFormatters();
  const navigate = useNavigate();
  const location = useLocation();
  const { staffId: staffIdFromParams } = useParams(); // For /staff/:staffId/edit route
  const [searchParams, setSearchParams] = useSearchParams();
  const [staff, setStaff] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [invitingStaffId, setInvitingStaffId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 100;
  
  // EF-FIX-CREW-ADMIN-STATUS-GROUPING-FULL-DATASET-V1: Server-side sorting state
  const [sortField, setSortField] = useState("last_name");
  const [sortDirection, setSortDirection] = useState("asc");

  // EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V2: Invite modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  // EVENTSFLO-CREW-PWA-ONBOARDING-REMINDER-AND-ADMIN-PENDING-DOC-ALERTS-V1
  // Tenant-wide pending document alert surfaced in the page header. Polls
  // every 60s while this page is open so newly uploaded crew docs appear
  // without a manual refresh.
  const [pendingAlert, setPendingAlert] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchAlert = async () => {
      try {
        const { data } = await api.get("/admin/dashboard/pending-document-alerts");
        if (!cancelled) setPendingAlert(data);
      } catch (e) {
        // Silent — header just won't show the alert pill.
        if (!cancelled) setPendingAlert(null);
      }
    };
    fetchAlert();
    const t = setInterval(fetchAlert, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom and wrap settings
  const { 
    currentZoom, scale, zoomIn, zoomOut, canZoomIn, canZoomOut,
    wrapText, toggleWrap 
  } = useTableDisplaySettings("staff-page");
  const zoomClasses = getZoomClasses(scale);

  // Platform Navigation Contract: Store returnUrl from location.state
  // This is the explicit return target provided by the launching context
  const [returnUrl, setReturnUrl] = useState(null);

  // EF-FIX-CREW-ADMIN-SERVER-SIDE-FILTERING-V1: Use unified /api/crew endpoint
  // EF-FIX-CREW-ADMIN-STATUS-GROUPING-FULL-DATASET-V1: Added sort parameters
  // Filtering, sorting, and pagination are now fully server-side
  const loadData = useCallback(async (page = 1, append = false, search = '', statusFilter = null, sortBy = null, sortDir = null) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      // Build query params for unified /api/crew endpoint
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', PAGE_SIZE.toString());
      
      // Search
      if (search) {
        params.append('search', search);
      }
      
      // Filter by status (server-side)
      if (statusFilter && statusFilter !== 'all') {
        params.append('filter', `status:${statusFilter}`);
      }
      
      // EF-FIX-CREW-ADMIN-STATUS-GROUPING-FULL-DATASET-V1: Server-side sorting
      // Use provided sort params, or default based on status filter
      const effectiveSortField = sortBy || (statusFilter === 'invited' || statusFilter === 'pending' ? 'created_at' : 'last_name');
      const effectiveSortDir = sortDir || (statusFilter === 'invited' || statusFilter === 'pending' ? 'desc' : 'asc');
      params.append('sort', `${effectiveSortField}:${effectiveSortDir}`);
      
      // Fetch from unified /api/crew endpoint
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('eventsflo_auth_token');
      
      // EF-ELIMINATE-ALL-FRONTEND-DATA-SOURCE-DEPENDENCY-V1: Log API call for verification
      const apiUrl = `${API_URL}/api/crew?${params.toString()}`;
      console.log('[CREW API] Fetching:', apiUrl);
      
      const [crewRes, venuesRes] = await Promise.all([
        fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()),
        page === 1 && !search ? getVenues() : Promise.resolve({ data: venues }),
      ]);
      
      // EF-ELIMINATE-ALL-FRONTEND-DATA-SOURCE-DEPENDENCY-V1: Log response for verification
      console.log('[CREW API] Response:', { 
        total: crewRes.meta?.total, 
        count: crewRes.data?.length,
        firstRecord: crewRes.data?.[0]?.last_name 
      });
      
      // Handle unified response format: { data: [...], meta: { page, limit, total, total_pages } }
      const crewData = crewRes.data || [];
      const meta = crewRes.meta || { page: 1, limit: PAGE_SIZE, total: crewData.length, total_pages: 1 };
      
      if (append) {
        setStaff(prev => [...prev, ...crewData]);
      } else {
        setStaff(crewData);
      }
      
      setTotalCount(meta.total);
      setHasMore(meta.page < meta.total_pages);
      
      if (page === 1 && !search) {
        setVenues(venuesRes.data);
      }
      setCurrentPage(meta.page);
      
      return crewData;
    } catch (err) {
      console.error('Failed to load crew data:', err);
      toast.error("Failed to load data");
      return [];
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [venues]);

  // Initial load and debounced search
  // EF-FIX-CREW-ADMIN-SERVER-SIDE-FILTERING-V1: Use page 1 (1-indexed)
  // EF-FIX-CREW-ADMIN-STATUS-GROUPING-FULL-DATASET-V1: Also re-fetch on sort change
  // EF-FIX-INVITED-SORT-AND-DOCUMENT-PLATFORM-RULES-V1: Auto-set sort for invited/pending
  // EF-ELIMINATE-ALL-FRONTEND-DATA-SOURCE-DEPENDENCY-V1: Force API call on every filter change
  useEffect(() => {
    // EF-ELIMINATE-ALL-FRONTEND-DATA-SOURCE-DEPENDENCY-V1: Clear data immediately to prevent stale display
    setStaff([]);
    setTotalCount(0);
    setHasMore(false);
    
    // Determine appropriate sort based on filter
    let effectiveSortField = sortField;
    let effectiveSortDirection = sortDirection;
    
    // Auto-set sort for invited/pending to created_at:desc (newest first)
    if (filterStatus === 'invited' || filterStatus === 'pending') {
      effectiveSortField = 'created_at';
      effectiveSortDirection = 'desc';
    }
    
    // EF-ELIMINATE-ALL-FRONTEND-DATA-SOURCE-DEPENDENCY-V1: ALWAYS call API - no conditions that skip
    const timer = setTimeout(() => {
      console.log(`[API CALL] /api/crew?filter=status:${filterStatus}&sort=${effectiveSortField}:${effectiveSortDirection}`);
      loadData(1, false, searchTerm, filterStatus, effectiveSortField, effectiveSortDirection);
    }, searchTerm ? 300 : 0); // No delay on initial load, 300ms debounce for search
    
    return () => clearTimeout(timer);
  }, [searchTerm, filterStatus]); // Re-fetch when filter or search changes
  
  // EF-ELIMINATE-ALL-FRONTEND-DATA-SOURCE-DEPENDENCY-V1: Separate effect for sort changes (only for active/inactive)
  useEffect(() => {
    // Skip if filter is invited/pending (sort is auto-determined)
    if (filterStatus === 'invited' || filterStatus === 'pending') {
      return;
    }
    
    // Clear and refetch with new sort
    setStaff([]);
    console.log(`[API CALL - Sort Change] /api/crew?filter=status:${filterStatus}&sort=${sortField}:${sortDirection}`);
    loadData(1, false, searchTerm, filterStatus, sortField, sortDirection);
  }, [sortField, sortDirection]);

  // Platform Navigation Contract: Handle dialog close with explicit navigation
  // No history-based navigation (navigate(-1), history.back) is permitted
  const handleDialogClose = useCallback((open) => {
    setDialogOpen(open);
    if (!open && returnUrl) {
      // Dialog is closing and we have a return URL - navigate to explicit target
      navigate(returnUrl);
      setReturnUrl(null);
    } else if (!open && staffIdFromParams) {
      // If we came via /crew-management/:id/edit route without explicit returnUrl, go to /crew-management (degraded path)
      navigate('/crew-management');
    }
  }, [returnUrl, navigate, staffIdFromParams]);

  // Platform Navigation Contract: Handle edit via URL path or location state
  // Supports both /staff/:staffId/edit route pattern and legacy ?edit= query param
  useEffect(() => {
    // Check for returnUrl in location.state (new pattern from Shift Crew Planner)
    const stateReturnUrl = location.state?.returnUrl;
    
    // Use staffId from useParams (preferred) or fall back to legacy query param
    const editStaffId = staffIdFromParams || searchParams.get("edit");
    const returnUrlFromQuery = searchParams.get("returnUrl");
    
    if (editStaffId) {
      // Store return URL if provided (prefer state over query param)
      if (stateReturnUrl) {
        setReturnUrl(stateReturnUrl);
      } else if (returnUrlFromQuery) {
        setReturnUrl(decodeURIComponent(returnUrlFromQuery));
      }
      
      // Fetch the crew member directly to ensure we get them even if not in current list
      const loadStaffForEdit = async () => {
        try {
          const response = await getStaff(editStaffId);
          if (response.data) {
            setSelectedStaff(response.data);
            setDialogOpen(true);
            // Clear the query parameters (if using legacy pattern)
            if (searchParams.get("edit")) {
              setSearchParams({});
            }
          }
        } catch (err) {
          console.error("Failed to load staff for edit:", err);
          toast.error("Could not find crew member");
          // Navigate to fallback (degraded path)
          if (stateReturnUrl || returnUrlFromQuery) {
            navigate(stateReturnUrl || decodeURIComponent(returnUrlFromQuery));
          } else {
            navigate('/crew-management');
          }
          setReturnUrl(null);
        }
      };
      loadStaffForEdit();
    }
  }, [staffIdFromParams, location.state, searchParams, setSearchParams, navigate]);

  const handleSendInvitation = async (member) => {
    if (!member.email) {
      toast.error("Crew member has no email address");
      return;
    }
    
    setInvitingStaffId(member.id);
    try {
      const res = await sendCrewInvitation(member.id);
      toast.success(res.data.message || `Invitation sent to ${member.email}`);
      // Reload data to update invitation status
      await loadData(1, false, searchTerm);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setInvitingStaffId(null);
    }
  };

  const handleOpenDialog = (member = null) => {
    setSelectedStaff(member);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteStaff(selectedStaff.id);
      // EF-ADMIN-SOFT-DELETE-CREW-USER-V1: Updated messaging for soft delete
      toast.success("Crew member deactivated successfully");
      setDeleteDialogOpen(false);
      setSelectedStaff(null);
      loadData(1, false, searchTerm);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // EF-FIX-CREW-ADMIN-SERVER-SIDE-FILTERING-V1: REMOVED frontend filtering
  // All filtering is now done server-side via /api/crew endpoint
  // DO NOT filter or sort data on the frontend - render exactly as returned from API
  
  // EF-FIX-CREW-ADMIN-STATUS-GROUPING-FULL-DATASET-V1: Server-side sort handler
  const handleServerSort = useCallback((field, direction) => {
    if (direction === null) {
      // Reset to default sort
      setSortField("last_name");
      setSortDirection("asc");
    } else {
      setSortField(field);
      setSortDirection(direction);
    }
    // loadData will be triggered by useEffect when sortField/sortDirection changes
  }, []);

  const columns = [
    {
      key: "name",
      sortKey: "last_name", // Sort by last name
      label: "Name",
      render: (_, row) => (
        <div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(row);
            }}
            className="font-medium text-left hover:text-teal-400 hover:underline transition-colors"
            data-testid={`edit-name-${row.id}`}
          >
            {row.first_name} {row.last_name}
          </button>
          {row.employee_id && (
            <div className="text-[10px] text-muted-foreground font-mono">{row.employee_id}</div>
          )}
        </div>
      ),
    },
    { 
      key: "email", 
      label: "Contact",
      render: (_, row) => (
        <div className="text-sm">
          <div className="flex items-center gap-1">
            <Mail className="h-3 w-3 text-muted-foreground" />
            {row.email}
          </div>
          {row.phone && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Phone className="h-3 w-3" />
              {row.phone}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "employment_type",
      label: "Type",
      render: (value) => (
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          {value?.replace("_", " ") || "Casual"}
        </span>
      ),
    },
    {
      key: "hourly_rate_default",
      label: "Rate",
      align: "right",
      render: (value) => value ? <span className="font-mono text-sm">{formatCurrency(value)}/hr</span> : "-",
    },
    {
      key: "right_to_work_status",
      label: "RTW",
      render: (value) => <RTWStatusBadge status={value || "not_checked"} />,
    },
    {
      key: "skills",
      label: "Skills",
      render: (value) =>
        value?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {value.slice(0, 2).map((skill) => (
              <span
                key={skill}
                className="px-2 py-0.5 bg-primary/10 text-primary text-[10px] rounded-full uppercase tracking-wider"
              >
                {skill}
              </span>
            ))}
            {value.length > 2 && (
              <span className="text-muted-foreground text-xs">+{value.length - 2}</span>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "status",
      label: "Status",
      render: (value) => <StatusBadge status={value} />,
    },
    // EF-CREW-READINESS-STATUS-V1: Readiness column
    {
      key: "readiness_status",
      label: "Readiness",
      render: (value, row) => (
        <ReadinessStatusBadge 
          status={value || "missing_documents"} 
          reason={row.readiness_reason}
        />
      ),
    },
    {
      key: "invitation_status",
      sortKey: "invite_status",  // EF-FIX-INVITE-COLUMN-SERVER-SIDE-SORT-V1: Server-side sorting
      sortable: true,  // EF-FIX-INVITE-COLUMN-SERVER-SIDE-SORT-V1: Enable sorting
      label: "Invite",
      render: (_, row) => {
        // EF-FIX-INVITE-COLUMN-SERVER-SIDE-SORT-V1: Use computed invite_status from backend if available
        const inviteStatus = row.invite_status || (row.pwa_first_accessed_at ? "active" : row.invitation_sent ? "invited" : "not_invited");
        
        if (inviteStatus === "active" || row.pwa_first_accessed_at) {
          return (
            <div className="flex items-center gap-1 text-green-400" title="Active - Has accessed PWA">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-xs">Active</span>
            </div>
          );
        }
        if (inviteStatus === "invited" || row.invitation_sent) {
          return (
            <div className="flex items-center gap-1 text-amber-400" title={`Invited ${row.invitation_sent_at ? new Date(row.invitation_sent_at).toLocaleDateString() : ''}`}>
              <MailCheck className="h-4 w-4" />
              <span className="text-xs">Invited</span>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-1 text-muted-foreground" title="Not invited">
            <Mail className="h-4 w-4" />
            <span className="text-xs">Not invited</span>
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleSendInvitation(row);
            }}
            title={row.invitation_sent ? "Re-send invitation" : "Send invitation"}
            data-testid={`invite-staff-${row.id}`}
            disabled={!row.email || invitingStaffId === row.id}
            className="text-teal-400 hover:text-teal-300 hover:bg-teal-900/20"
          >
            {invitingStaffId === row.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(row);
            }}
            title="Edit"
            data-testid={`edit-staff-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStaff(row);
              setDeleteDialogOpen(true);
            }}
            title="Deactivate crew member"
            data-testid={`delete-staff-${row.id}`}
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div data-testid="staff-page">
      {/* EF-GLOBAL-PAGE-ZOOM-FIX-V1: Wrap page content with zoom container */}
      <PageZoomContainer scale={scale}>
        <PageHeader
          title="Crew"
          subtitle="Manage crew members, compliance, and operational preferences"
          actions={
            <div className="flex items-center gap-2">
              {/* EVENTSFLO-CREW-PWA-ONBOARDING-REMINDER-AND-ADMIN-PENDING-DOC-ALERTS-V1
                  Pending document alert badge — visible whenever the tenant has at
                  least one crew document awaiting verification. Click filters the
                  Crew table so admins jump straight to who needs review. */}
              {pendingAlert?.pending_documents_count > 0 && (
                <Button
                  onClick={() => {
                    setFilterStatus("active");
                    setSearchTerm("");
                    toast.info(
                      `${pendingAlert.pending_documents_count} pending document(s) across ${pendingAlert.staff_with_pending_count} crew member(s).`
                    );
                  }}
                  variant="outline"
                  className="rounded-sm font-medium tracking-wide uppercase text-xs px-3 border-amber-500/50 text-amber-700 hover:bg-amber-500/10"
                  data-testid="admin-pending-docs-alert"
                  title={`${pendingAlert.pending_documents_count} pending across ${pendingAlert.staff_with_pending_count} crew`}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Pending Docs
                  <Badge
                    variant="secondary"
                    className="ml-2 bg-amber-500/15 text-amber-700 border-amber-500/40"
                    data-testid="admin-pending-docs-count"
                  >
                    {pendingAlert.pending_documents_count}
                  </Badge>
                </Button>
              )}
              {/* EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V2: Invite Crew Button */}
              <Button
                onClick={() => setInviteModalOpen(true)}
                variant="outline"
                className="rounded-sm font-medium tracking-wide uppercase text-xs px-4"
                data-testid="invite-crew-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Crew
              </Button>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
                data-testid="add-staff-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Crew Member
              </Button>
            </div>
          }
        />

        {/* Filters */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="staff-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="staff-filter-status">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="invited">Invited</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            {staff.length} of {totalCount} crew
            {hasMore && <span className="text-xs ml-1">(more available)</span>}
          </div>
          
          {/* EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom Controls */}
          <ZoomControl
            currentLabel={currentZoom.label}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
            wrapText={wrapText}
            onToggleWrap={toggleWrap}
          />
        </div>

        <div className="bg-card border border-white/5 rounded-sm">
          {staff.length === 0 ? (
            <EmptyState
              icon={UserCheck}
              title="No crew yet"
              description="Add crew members to assign them to shifts. You can import crew from a CSV or add them manually."
              action={
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-primary text-primary-foreground"
                  data-testid="empty-add-staff-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Crew Member
                </Button>
              }
            />
          ) : staff.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No crew match your search criteria</p>
            </div>
          ) : (
            <>
              <div 
                className={`${!wrapText ? '[&_td]:whitespace-nowrap [&_td]:overflow-hidden [&_td]:text-ellipsis' : ''}`}
              >
                {/* EF-FIX-CREW-ADMIN-STATUS-GROUPING-FULL-DATASET-V1: Use server-side sorting */}
                <DataTable 
                  columns={columns} 
                  data={staff}
                  onSort={handleServerSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  disableLocalSort={true}
                />
              </div>
              {/* Load More Button */}
              {hasMore && (
                <div className="p-4 border-t border-white/5 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => loadData(currentPage + 1, true, searchTerm, filterStatus, sortField, sortDirection)}
                    disabled={loadingMore}
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        Load More ({totalCount - staff.length} remaining)
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </PageZoomContainer>

      {/* Crew Form Dialog - Use new tabbed UI */}
      {USE_NEW_CREW_PROFILE_UI ? (
        <CrewProfileTabs
          open={dialogOpen}
          onOpenChange={handleDialogClose}
          staff={selectedStaff}
          onSuccess={() => {
            loadData(1, false, searchTerm);
          }}
          venues={venues}
        />
      ) : (
        <CrewFormDialog
          open={dialogOpen}
          onOpenChange={handleDialogClose}
          staff={selectedStaff}
          onSuccess={() => {
            loadData(1, false, searchTerm);
          }}
          venues={venues}
        />
      )}

      {/* EF-ADMIN-SOFT-DELETE-CREW-USER-V1: Deactivate Confirmation */}
      <DestructiveConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Deactivate Crew Member"
        itemName={`${selectedStaff?.first_name} ${selectedStaff?.last_name}`}
        itemType="crew member"
        description={`Are you sure you want to deactivate ${selectedStaff?.first_name} ${selectedStaff?.last_name}? They will no longer appear in crew lists and will be unable to log in. All historical data (documents, shifts, assignments) will be preserved. This can be reversed by contacting support.`}
        onConfirm={handleDelete}
        loading={deleting}
      />

      {/* EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V2: Invite Crew Modal */}
      <InviteCrewModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        onSuccess={() => {
          // Refresh crew list and switch to "invited" filter
          setFilterStatus("invited");
          loadData(1, false, "", "invited");
        }}
      />
    </div>
  );
}
